import { AxiosResponse } from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { useLambdaFunctionUrl } from '../../../hooks/useEmailSettingLambda';
import API_ENDPOINTS from '../../../apiEndpoints';
import { validateEmailSend } from '../utils/validateEmailSend';
import { showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { axiosInstanceForLambda } from '../../../library/Axios';
import { Typography } from '@mui/system';
import { Error404SnackbarComponent } from '../utils/snackBarComponent';
import { set } from 'lodash';

export type EmailStatus = 'pending' | 'sent';

export interface Recipient {
  id: number;
  recipient: Boolean;
  recipientId: number;
  name?: string;
  email: string;
  invoiceNumber?: string;
  isActive?: number;
}

export interface SendEmailHookOptions {
  initialPageType?: string;
  recipients: Recipient[];
  onClose?: () => void;
  onProgress?: (progress: { sentEmails: number; totalEmails: number; message: string; progressPercentage: number }) => void;
}

interface FinancialYear {
  startdate: number;
  enddate: number;
}

interface UserPreferenceAndReducer {
  financialYear?: FinancialYear;
}

interface RootState {
  userPrefrenceandReducer?: UserPreferenceAndReducer;
}

type EmailSendError = {
  status: number;
  message: string;
  response: AxiosResponse | any;
};

export type EmailSendResponse = {
  status: string; // "success" or "error"
  message: string;
  data: {
    summary: {
      total: number;
      success: number;
      failed: number;
    };
    details: {
      email: string;
      party: string;
      status: string; // e.g., "Success" or "Failed"
      message: string;
      vouchernumber?: string;
      recipient?: string;
    }[];
  };
} | null;

export type EmailSendData = NonNullable<EmailSendResponse>['data'];
export function useSendEmail({ initialPageType = '', recipients, onClose, onProgress }: SendEmailHookOptions = { recipients: [] }) {
  const [loading, setLoading] = useState(false);
  const [pageType, setPageType] = useState(initialPageType);
  const [statuses, setStatuses] = useState<Record<string, EmailStatus>>({});
  const [modalHeader, setModalHeader] = useState('');
  const [dateRange, setDateRange] = useState<{ from: number | null; to: number | null }>({ from: null, to: null });
  const [responseSummary, setResponseSummary] = useState<{ open: boolean; data: EmailSendResponse }>({ open: false, data: null });
  const { callLambdaFunctionUrl } = useLambdaFunctionUrl();

  const { startdate, enddate } = useSelector((state: RootState) => state.userPrefrenceandReducer?.financialYear || {}) as FinancialYear;

  useEffect(() => {
    setPageType(initialPageType);
  }, [initialPageType]);

  useEffect(() => {
    switch (pageType) {
      case 'customer':
        setModalHeader('Send Transactional Email to Customers');
        break;
      case 'supplier':
        setModalHeader('Send Transactional Email to Suppliers');
        break;
      case 'salesInvoice':
        setModalHeader('Send Invoice Email');
        break;
      case 'purchaseInvoice':
        setModalHeader('Send Purchase Invoice Email');
        break;
      case 'receipt':
        setModalHeader('Send Receipt Email');
        break;
      case 'payment':
        setModalHeader('Send Payment Confirmation Email');
        break;
      case 'creditNote':
        setModalHeader('Send Credit Note Email');
        break;
      case 'debitNote':
        setModalHeader('Send Debit Note Email');
        break;
      case 'pos':
        setModalHeader('Send POS Invoice Email');
        break;
      default:
        setModalHeader('');
    }
  }, [pageType]);

  const updateStatus = useCallback((id: string, status: EmailStatus) => {
    setStatuses((prev) => ({ ...prev, [id]: status }));
  }, []);

  const handleDateRangeChange = useCallback((newRange: { from: number | null; to: number | null }) => {
    setDateRange(newRange);
  }, []);

  const handleSendEmail = useCallback(async () => {
    setLoading(true);
    const activeRecipients = recipients.filter((r) => r.isActive === 1);
    const { valid, error } = validateEmailSend(activeRecipients, pageType, dateRange);

    if (!valid) {
      showErrorSnackbar(error || '', 'right', 'bottom');
      setLoading(false);
      return;
    }

    // Smooth progress animation function
    let currentProgress = 0;
    
    const animateProgress = (targetProgress: number, message: string, duration: number = 1000) => {
      return new Promise<void>((resolve) => {
        if (!onProgress) {
          resolve();
          return;
        }

        const startTime = Date.now();
        const startProgress = currentProgress;
        
        const updateProgress = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Easing function for smooth animation (ease-out)
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          const newProgress = startProgress + (targetProgress - startProgress) * easedProgress;
          currentProgress = newProgress;
          
          const sentEmails = Math.floor((currentProgress / 100) * activeRecipients.length);
          
          onProgress({
            sentEmails: Math.floor((currentProgress / 100) * activeRecipients.length),
            totalEmails: activeRecipients.length,
            message,
            progressPercentage: currentProgress,
          });
          
          if (progress < 1) {
            requestAnimationFrame(updateProgress);
          } else {
            currentProgress = targetProgress;
            resolve();
          }
        };
        
        requestAnimationFrame(updateProgress);
      });
    };

    // Stage 1: Initialization (0% to 15%)
    await animateProgress(15, 'Preparing to send emails...', 800);

    // Check if we have POS invoices and adjust the type accordingly
    const hasPOSInvoices = activeRecipients.some((r) => r.invoiceNumber?.startsWith('POS'));
    
    const type = hasPOSInvoices 
      ? 'pos'  // Use 'pos' type for POS invoices
      : {
          customer: 'transaction',
          supplier: 'transaction',
          salesInvoice: 'invoice',
          purchaseInvoice: 'purchase',
          receipt: 'receipt',
          creditNote: 'creditnote',
          debitNote: 'debitnote',
        }[initialPageType] || 'paymentconfirm';

    const ids: (string | number)[] = activeRecipients.map((r) => r.id ?? '');

    if (ids.length === 0) {
      showWarningSnackbar('No valid recipient IDs available');
      setLoading(false);
      return;
    }

    // Stage 2: Connecting to service (15% to 30%)
    await animateProgress(30, 'Connecting to email service...', 600);

    // For POS invoices, we need to include email addresses in the payload
    const posRecipients = activeRecipients.filter((r) => r.invoiceNumber?.startsWith('POS'));
    const regularRecipients = activeRecipients.filter((r) => !r.invoiceNumber?.startsWith('POS'));
    
    let payload;
    if (posRecipients.length > 0) {
      // For POS invoices, include email addresses in the payload
      payload = {
        ...buildEmailPayload(type, ids, {
          startDate: dateRange.from || undefined,
          endDate: dateRange.to || undefined,
        }),
        posEmails: posRecipients.map((r) => ({
          invoiceid: r.id,
          email: r.email
        }))
      };
    } else {
      // For regular invoices, use the standard payload
      payload = buildEmailPayload(type, ids, {
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      });
    }

    // Stage 3: Processing recipients (30% to 45%)
    await animateProgress(45, 'Processing recipients...', 700);

    // Stage 4: Preparing API call (45% to 60%)
    await animateProgress(60, 'Preparing email data...', 500);

    // Start the actual API call
    const apiCallPromise = axiosInstanceForLambda({
      url: API_ENDPOINTS.EMAIL_SENDING_URL,
      data: payload,
      method: 'POST',
    });

    // Stage 5: API call in progress (60% to 85%) - animate while waiting
    const apiProgressPromise = animateProgress(85, 'Sending emails...', 2000);

    // Wait for both API call and progress animation
    await Promise.all([apiCallPromise, apiProgressPromise]);

    // Handle API response
    try {
      const response = await apiCallPromise;
      let data = response.data as EmailSendResponse;
      
      // Stage 6: Completion (85% to 100%)
      await animateProgress(100, 'Emails sent successfully!', 800);
      
      setResponseSummary({ open: true, data: data || null });
      setLoading(false);
    } catch (error) {
      const statusCode = (error as any)?.response?.status || 500;
      if (statusCode === 404) {
        Error404SnackbarComponent({ message: (error as any)?.response?.data?.message });
      } else {
        showErrorSnackbar((error as any)?.response?.data?.message || 'Failed to send email', 'right', 'bottom');
      }
      setLoading(false);
    }
  }, [initialPageType, recipients, dateRange, callLambdaFunctionUrl, onProgress]);

  const buildEmailPayload = (
    type: string,
    ids: Array<string | number>,
    params?: {
      startDate?: number;
      endDate?: number;
    }
  ): {
    ids: Array<string | number>;
    startDate?: number;
    endDate?: number;
    type: string;
  } => {
    return type === 'transaction'
      ? {
          ids,
          startDate: params?.startDate || undefined,
          endDate: params?.endDate || undefined,
          type,
        }
      : {
          ids,
          type,
        };
  };

  const resetAllStates = useCallback(() => {
    setLoading(false);
    setPageType(initialPageType);
    setStatuses({});
    setDateRange({ from: null, to: null });
    onClose?.();
  }, [initialPageType, onClose]);

  const handleCloseWrapperModal = useCallback(() => {
    setResponseSummary({ open: false, data: null });
    resetAllStates();
  }, [resetAllStates]);

  const handleCloseResponseSummary = useCallback(() => {
    setResponseSummary((prev) => ({ ...prev, open: false, data: null }));
    resetAllStates();
  }, [resetAllStates]);

  return {
    loading,
    setLoading,
    pageType,
    setPageType,
    statuses,
    updateStatus,
    dateRange,
    handleDateRangeChange,
    modalHeader,
    handleSendEmail,
    responseSummary,
    handleCloseResponseSummary,
    startdate,
    enddate,
    handleCloseWrapperModal,
  };
}
